import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateListingInput,
  ListingCard,
  ListingDetail,
  ListingSearchQuery,
  ListingStatus,
  OwnListingCard,
  PaginationMeta,
  PublicListingDetail,
} from '@biyolink/shared';
import { apiClient, getData, patchData, postData } from './client';

export const listingKeys = {
  mine: (status: ListingStatus | 'ALL', page: number) => ['listings', 'me', status, page] as const,
  counts: ['listings', 'me', 'counts'] as const,
  detail: (id: string) => ['listings', 'me', 'detail', id] as const,
  public: (slug: string) => ['listings', 'public', slug] as const,
  search: (params: Record<string, unknown>) => ['listings', 'search', params] as const,
  featured: ['listings', 'featured'] as const,
};

/** Vitrin araması. `params` doğrudan sorgu dizesine çevrilir. */
export function useListingSearch(params: Partial<ListingSearchQuery> & Record<string, unknown>) {
  return useQuery({
    queryKey: listingKeys.search(params),
    queryFn: async (): Promise<{ items: ListingCard[]; meta: PaginationMeta }> => {
      const response = await apiClient.get<{ data: ListingCard[]; meta: PaginationMeta }>(
        '/listings',
        { params },
      );
      return { items: response.data.data, meta: response.data.meta };
    },
    // Filtre değişince önceki sonuç ekranda kalsın, liste zıplamasın.
    placeholderData: (previous) => previous,
  });
}

/** Ana sayfadaki "öne çıkan ilanlar" şeridi (son eklenen 8 ilan). */
export function useFeaturedListings() {
  return useQuery({
    queryKey: listingKeys.featured,
    queryFn: async (): Promise<ListingCard[]> => {
      const response = await apiClient.get<{ data: ListingCard[] }>('/listings', {
        params: { limit: 8, sort: 'newest' },
      });
      return response.data.data;
    },
    staleTime: 60_000,
  });
}

/**
 * İlan detayını gerektiği anda getirir (önbellekte varsa oradan). Liste satırındaki
 * "Fiyat teklifle belirlenir" düğmesi teklif penceresini ilan sayfasına gitmeden
 * açarken kullanır — pencere detaydaki teslimat seçeneklerine ihtiyaç duyuyor.
 */
export function usePublicListingLoader(): (slug: string) => Promise<PublicListingDetail> {
  const queryClient = useQueryClient();
  return (slug) =>
    queryClient.fetchQuery({
      queryKey: listingKeys.public(slug),
      queryFn: () => getData<PublicListingDetail>(`/listings/${slug}`),
    });
}

export function usePublicListing(slug: string | undefined) {
  return useQuery({
    queryKey: listingKeys.public(slug ?? ''),
    queryFn: () => getData<PublicListingDetail>(`/listings/${slug}`),
    enabled: Boolean(slug),
  });
}

export function useMyListings(status: ListingStatus | 'ALL', page = 1) {
  return useQuery({
    queryKey: listingKeys.mine(status, page),
    queryFn: async (): Promise<{ items: OwnListingCard[]; meta: PaginationMeta }> => {
      const response = await apiClient.get<{ data: OwnListingCard[]; meta: PaginationMeta }>(
        '/listings/me',
        { params: { page, ...(status === 'ALL' ? {} : { status }) } },
      );
      return { items: response.data.data, meta: response.data.meta };
    },
  });
}

export function useMyListingCounts() {
  return useQuery({
    queryKey: listingKeys.counts,
    queryFn: () => getData<Record<string, number>>('/listings/me/counts'),
  });
}

export function useMyListing(id: string | undefined) {
  return useQuery({
    queryKey: listingKeys.detail(id ?? ''),
    queryFn: () => getData<ListingDetail>(`/listings/me/${id}`),
    enabled: Boolean(id),
  });
}

/** İlan tüm verisini ve görsellerini tek multipart istekte gönderir. */
export function useCreateListing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { payload: CreateListingInput; images: File[] }) => {
      const formData = new FormData();
      formData.append('payload', JSON.stringify(input.payload));
      for (const image of input.images) {
        formData.append('images', image);
      }

      const response = await apiClient.post<{ data: ListingDetail }>('/listings', formData);
      return response.data.data;
    },
    onSuccess: () => invalidateListings(queryClient),
  });
}

export function useUpdateListing(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateListingInput) =>
      patchData<ListingDetail>(`/listings/${id}`, payload),
    onSuccess: (listing) => {
      queryClient.setQueryData(listingKeys.detail(id), listing);
      void invalidateListings(queryClient);
    },
  });
}

type ListingAction = 'pause' | 'activate' | 'renew';

export function useListingAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; action: ListingAction }) =>
      postData<ListingDetail>(`/listings/${input.id}/${input.action}`),
    onSuccess: () => invalidateListings(queryClient),
  });
}

export function useDeleteListing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/listings/${id}`);
    },
    onSuccess: () => invalidateListings(queryClient),
  });
}

export function useAddListingImages(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (images: File[]) => {
      const formData = new FormData();
      for (const image of images) formData.append('images', image);
      const response = await apiClient.post<{ data: ListingDetail }>(
        `/listings/${id}/images`,
        formData,
      );
      return response.data.data;
    },
    onSuccess: (listing) => {
      queryClient.setQueryData(listingKeys.detail(id), listing);
      void invalidateListings(queryClient);
    },
  });
}

export function useDeleteListingImage(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (imageId: string) => {
      const response = await apiClient.delete<{ data: ListingDetail }>(
        `/listings/${id}/images/${imageId}`,
      );
      return response.data.data;
    },
    onSuccess: (listing) => queryClient.setQueryData(listingKeys.detail(id), listing),
  });
}

export function useReorderListingImages(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (imageIds: string[]) =>
      patchData<ListingDetail>(`/listings/${id}/images/order`, { imageIds }),
    onSuccess: (listing) => {
      queryClient.setQueryData(listingKeys.detail(id), listing);
      void invalidateListings(queryClient);
    },
  });
}

export function useUploadAnalysisReport(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<{ data: ListingDetail }>(
        `/listings/${id}/analysis-report`,
        formData,
      );
      return response.data.data;
    },
    onSuccess: (listing) => queryClient.setQueryData(listingKeys.detail(id), listing),
  });
}

async function invalidateListings(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: ['listings'] });
}
